import { useRef, useState } from 'react';
import { CAPIUM_TAG_GROUPS, appendCapiumTag } from '@/core/models/capium-tags';
import type { CapiumTagGroup } from '@/core/models/capium-tags';
import { Button } from '@/kit/Button';
import { NoteTagsTab } from '@/features/settings-b/NoteTagsTab';
import { DialogClose, DialogContent, type DialogComponentProps } from '@/kit/dialog';
import { Icon } from '@/kit/Icon';
import { RichTextEditor } from '@/kit/RichTextEditor';
import { standardNoteEditorConfig } from './noteEditorConfig';
import './RelatedNoteDialog.scss';

const editorConfig = standardNoteEditorConfig();

export interface RelatedNoteDialogData {
  /** The note's fixed name (e.g. "Opinion"); shown as the dialog heading, not editable here. */
  heading: string;
  content: string;
  /** AP parity: when given, "Reset Default" puts the catalogue wording back into the editor. */
  defaultContent?: string | null;
  tagGroups?: CapiumTagGroup[];
}

/**
 * APT256: the "Edit" icon beside a Related Notes checkbox opens its content here, in a larger dedicated view, for
 * notes too long to read comfortably in the card's inline box. Only the wording is edited - the note's name/title
 * isn't, so unlike PrinciplesActivitesDialog there is no Title field.
 */
export function RelatedNoteDialog({ data, dialogRef }: DialogComponentProps<RelatedNoteDialogData, string>) {
  const [content, setContent] = useState(data?.content ?? '');
  const [busy, setBusy] = useState(false);
  const closing = useRef(false);
  const onSave = () => {
    if (closing.current) return;
    closing.current = true;
    setBusy(true);
    dialogRef.close(content);
  };
  const insertTag = (tag: string) => setContent((current) => appendCapiumTag(current, tag));

  return (
    <div className="mat-dialog-wrapper">
      <DialogContent>
        <form noValidate onSubmit={(event) => event.preventDefault()}>
          <div className="main-contain">
            <div className="sidenav-header">
              <div className="d-flex align-items-center justify-space-between">
                <h2 className="m-0">
                  <span>{data?.heading || 'Note'}</span>
                </h2>
                <DialogClose>
                  <Icon name="close" className="close" />
                </DialogClose>
              </div>
            </div>
          </div>
          <div className="d-flex gap-15">
            <div className="form-field w-510 related-note-dialog-editor">
              <p className="fw-bold">Note Contents</p>
              <RichTextEditor id="relatedNoteEditor" value={content} onChange={setContent} config={editorConfig} />
              {data?.defaultContent != null && (
                <div className="d-flex justify-content-end pt-5">
                  <a className="link hyperlink" onClick={() => setContent(data.defaultContent ?? '')}>
                    Reset Default
                  </a>
                </div>
              )}
            </div>
            <NoteTagsTab groups={data?.tagGroups ?? CAPIUM_TAG_GROUPS} onInsert={insertTag} />
          </div>
          <div className="d-flex gap-15 hr-top pt-1 mt-1">
            <Button className="action-button primary-button" busy={busy} onClick={onSave}>
              Save
            </Button>
            <DialogClose as="button" type="button" className="action-button secondary-button">
              Cancel
            </DialogClose>
          </div>
        </form>
      </DialogContent>
    </div>
  );
}
